// Which phone, which browser, installed or not — the facts the install and notification steps
// depend on. One reading, so the install card and the notification card can never disagree about
// what device they are on.
//
// User-agent sniffing is the honest tool here, not a shortcut: whether "Add to Home Screen" exists,
// whether Web Push exists outside an installed app, and which menu to name are properties of the
// browser and OS, and no feature test answers them before the person has already tried and failed.

export type OS = "ios" | "ipados" | "android" | "desktop";
export type Browser = "safari" | "chrome" | "firefox" | "edge" | "samsung" | "inapp" | "other";

export interface Platform {
  os: OS;
  browser: Browser;
  /** iOS/iPadOS major.minor as a number (16.4 → 16.4), when known. */
  iosVersion: number | null;
  /** Running as an installed app (home screen / app window), not in a browser tab. */
  standalone: boolean;
  /** This browser can show web push at all, right now, in this context. */
  pushAvailable: boolean;
}

// In-app browsers (Telegram, Facebook, Instagram, Gmail/Google app, LINE, WhatsApp…) cannot add to
// the home screen and usually cannot ask for notifications: a link opened from a chat lands in one.
const IN_APP = /\b(FBAN|FBAV|Instagram|Line\/|GSA\/|Telegram|WhatsApp|MicroMessenger|Snapchat|TikTok|; wv\))/i;

export function detectPlatform(
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
  env: { maxTouchPoints?: number; standalone?: boolean; pushAvailable?: boolean } = {},
): Platform {
  const touch = env.maxTouchPoints ?? (typeof navigator !== "undefined" ? navigator.maxTouchPoints : 0);
  let os: OS = "desktop";
  if (/iPhone|iPod/.test(ua)) os = "ios";
  else if (/iPad/.test(ua) || (/Macintosh/.test(ua) && touch > 1)) os = "ipados"; // iPadOS 13+ says "Macintosh"
  else if (/Android/.test(ua)) os = "android";

  let browser: Browser = "other";
  if (IN_APP.test(ua)) browser = "inapp";
  else if (/SamsungBrowser\//.test(ua)) browser = "samsung";
  else if (/EdgiOS\/|EdgA\/|Edg\//.test(ua)) browser = "edge";
  else if (/FxiOS\/|Firefox\//.test(ua)) browser = "firefox";
  else if (/CriOS\/|Chrome\//.test(ua)) browser = "chrome";
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) browser = "safari";
  // An installed iOS web app reports no "Version/… Safari/" — it is Safari's engine, standalone.
  else if ((os === "ios" || os === "ipados") && /AppleWebKit/.test(ua)) browser = "safari";

  let iosVersion: number | null = null;
  const m = /OS (\d+)[_.](\d+)/.exec(ua);
  if ((os === "ios" || os === "ipados") && m) iosVersion = Number(m[1]) + Number(m[2]) / 10;
  const v = /Version\/(\d+)\.(\d+)/.exec(ua);
  if (os === "ipados" && iosVersion == null && v) iosVersion = Number(v[1]) + Number(v[2]) / 10;

  const standalone =
    env.standalone ??
    (typeof window !== "undefined" &&
      ((window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
        (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches)));

  const pushAvailable =
    env.pushAvailable ??
    (typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator && "PushManager" in window);

  return { os, browser, iosVersion, standalone: !!standalone, pushAvailable: !!pushAvailable };
}

/** Apple added Web Push for home-screen apps in iOS/iPadOS 16.4. */
export const IOS_PUSH_MIN = 16.4;

export function isApple(p: Platform): boolean {
  return p.os === "ios" || p.os === "ipados";
}
